/**
 * Marketing Manager Command Center (Task 205) — pure presentation logic,
 * extracted from `MarketingManagerCommandCenter.tsx` so it is unit-testable
 * without importing React. No fetching, no permission gating — display
 * mapping only. The FE gate (marketing_manager) lives in `AppLanding`; RLS
 * stays the load-bearing security gate.
 *
 * The small status→label / status→risk / Arabic-numeral display helpers are
 * the SAME primitives the Owner Command Center uses, so this module re-exports
 * them from `ownerCommandCenter.logic` rather than duplicating them (clean DRY
 * reuse of pure functions — no refactor of the owner surface). Only the
 * marketing-operations decision builder below is MM-specific.
 */

import type { ClientRow } from '../api/types';

export {
  SOON,
  clientStatusLabelAr,
  statusBadgeClass,
  statusRisk,
  statusWeight,
  avatarColor,
  initial,
  toArabicDigits,
  AVATAR_COLORS,
} from './ownerCommandCenter.logic';

/** Icons used by derived operational decisions (a subset of the MM icon set). */
export type MmDecisionIcon = 'megaphone' | 'calendar';

export interface OperationalDecision {
  readonly key: string;
  readonly tone: string;
  readonly sev: 'urgent' | 'high' | 'mid';
  readonly sevLabel: string;
  readonly icon: MmDecisionIcon;
  readonly title: string;
  readonly why: string;
  readonly to: string;
  readonly action: string;
}

/**
 * Build the "operational decisions needed today" list from REAL client status
 * only — paused accounts (urgent marketing review) and onboarding accounts
 * (mid: stand up the first campaigns / content plan). No fabricated signals;
 * every item links to the real client overview. Capped at 6. This is the
 * marketing-operations counterpart to the owner dashboard's `buildDecisions`
 * (different copy/intent), deliberately kept separate so the two dashboards
 * can diverge as real data lands.
 */
export function buildOperationalDecisions(
  clients: ReadonlyArray<ClientRow>,
): ReadonlyArray<OperationalDecision> {
  const out: OperationalDecision[] = [];
  for (const c of clients) {
    if (c.status === 'paused') {
      out.push({
        key: `paused-${c.id}`,
        tone: 'occ-dec-rose',
        sev: 'urgent',
        sevLabel: 'عاجل',
        icon: 'megaphone',
        title: `حساب متوقّف يحتاج مراجعة تسويقية — ${c.name}`,
        why: 'العميل متوقّف حالياً؛ راجعي حملاته ومحتواه وحدّدي خطة إعادة التفعيل قبل أن يتعثّر التسليم.',
        to: `/app/clients/${c.id}/overview`,
        action: 'فتح الحساب',
      });
    }
  }
  for (const c of clients) {
    if (c.status === 'onboarding') {
      out.push({
        key: `onb-${c.id}`,
        tone: 'occ-dec-plum',
        sev: 'mid',
        sevLabel: 'متوسط',
        icon: 'calendar',
        title: `عميل جديد يحتاج إعداداً تسويقياً — ${c.name}`,
        why: 'أطلقي خطة المحتوى والحملات الأولى وأسندي الإنتاج لتثبيت أهم شهر في علاقة العميل.',
        to: `/app/clients/${c.id}/overview`,
        action: 'بدء الإعداد',
      });
    }
  }
  return out.slice(0, 6);
}
